import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, KeyboardIcon } from 'lucide-react';
interface KeyboardShortcutsProps {
  onSearch: () => void;
  onEdit: () => void;
  onExport: () => void;
  onDarkMode: () => void;
}
export const KeyboardShortcuts = ({
  onSearch,
  onEdit,
  onExport,
  onDarkMode
}: KeyboardShortcutsProps) => {
  const [showHelp, setShowHelp] = useState(false);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modifier = isMac ? e.metaKey : e.ctrlKey;
      // Cmd/Ctrl + K - Focus search
      if (modifier && e.key === 'k') {
        e.preventDefault();
        onSearch();
      }
      // Cmd/Ctrl + E - Toggle edit mode
      if (modifier && e.key === 'e') {
        e.preventDefault();
        onEdit();
      }
      // Cmd/Ctrl + P - Export PDF
      if (modifier && e.key === 'p') {
        e.preventDefault();
        onExport();
      }
      // Cmd/Ctrl + D - Toggle dark mode
      if (modifier && e.key === 'd') {
        e.preventDefault();
        onDarkMode();
      }
      // ? - Show help
      if (e.key === '?' && !e.shiftKey) {
        setShowHelp(true);
      }
      // Escape - Close help
      if (e.key === 'Escape') {
        setShowHelp(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onSearch, onEdit, onExport, onDarkMode]);
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const modifierKey = isMac ? '⌘' : 'Ctrl';
  const shortcuts = [{
    key: `${modifierKey} K`,
    action: 'Focus Search'
  }, {
    key: `${modifierKey} E`,
    action: 'Toggle Edit Mode'
  }, {
    key: `${modifierKey} P`,
    action: 'Export PDF'
  }, {
    key: `${modifierKey} D`,
    action: 'Toggle Dark Mode'
  }, {
    key: '?',
    action: 'Show Shortcuts'
  }, {
    key: 'Esc',
    action: 'Close Modal'
  }];
  return <>
      <button onClick={() => setShowHelp(true)} className="fixed bottom-6 left-6 h-14 w-14 rounded-full bg-white dark:bg-[#1A2332] border-2 border-[#7FE5A8] shadow-xl hover:shadow-2xl transition-all duration-300 flex items-center justify-center z-50 hover:scale-110" aria-label="Show keyboard shortcuts" data-id="element-88">
        <KeyboardIcon className="h-6 w-6 text-[#1A2332] dark:text-white" data-id="element-89" />
      </button>
      <AnimatePresence data-id="element-90">
        {showHelp && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowHelp(false)} data-id="element-91">
            <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} onClick={e => e.stopPropagation()} className="bg-white dark:bg-[#1A2332] rounded-2xl shadow-2xl max-w-md w-full p-8 relative" data-id="element-92">
              <button onClick={() => setShowHelp(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" data-id="element-93">
                <XIcon className="h-6 w-6" data-id="element-94" />
              </button>
              <div className="flex items-center mb-6" data-id="element-95">
                <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#7FE5A8] to-[#5FD88F] flex items-center justify-center mr-4" data-id="element-96">
                  <KeyboardIcon className="h-6 w-6 text-[#1A2332]" data-id="element-97" />
                </div>
                <h2 className="text-2xl font-bold text-[#1A2332] dark:text-white" data-id="element-98">
                  Keyboard Shortcuts
                </h2>
              </div>
              <div className="space-y-3" data-id="element-99">
                {shortcuts.map((shortcut, index) => <motion.div key={index} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: index * 0.05
            }} className="flex items-center justify-between p-3 bg-[#E8FAF1] dark:bg-[#2A3F5F] rounded-lg" data-id="element-100">
                    <span className="text-[#1A2332] dark:text-white font-medium" data-id="element-101">
                      {shortcut.action}
                    </span>
                    <kbd className="px-3 py-1.5 bg-white dark:bg-[#1A2332] border-2 border-gray-200 dark:border-[#7FE5A8] rounded-lg text-[#1A2332] dark:text-white font-mono text-sm font-semibold shadow-sm" data-id="element-102">
                      {shortcut.key}
                    </kbd>
                  </motion.div>)}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
};